import {Box, ClickAwayListener, Paper, Popper, PopperProps} from '@mui/material';
import AppBlinkingIconButton from 'components/buttons/AppBlinkingIconButton';
import {MainCard, Transitions} from 'components/Theme';
import {Timer1} from 'iconsax-react';
import {ItemDataResponse} from 'models/board';
import {useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {timeTrackerSelector} from 'store/timeTracker';
import {TimeTrackerCounter} from './components';
import TimeTrackerStartAndStopButton from './components/TimeTrackerStartAndStopButton';
import {AppTooltip} from 'components/toolip';

interface TimeTrackerPopoverProps {
    itemDataId: string;
    item: ItemDataResponse;
    disablePortal?: boolean;
    popperPlacement?: PopperProps['placement'];
    showPopper?: boolean;
}

export const TimeTrackerPopover = ({
    itemDataId,
    item,
    disablePortal = true,
    popperPlacement = 'bottom',
    showPopper = true,
}: TimeTrackerPopoverProps) => {
    const anchorRef = useRef<any>(null);
    const [open, setOpen] = useState(false);

    const handleToggle = () => {
        if (showPopper) {
            setOpen(prevOpen => !prevOpen);
        }
    };

    const {selectedTimeTrackInRedux, isConuterStarted} = useSelector(timeTrackerSelector);

    const isSelectITemData = useMemo((): boolean => {
        if (selectedTimeTrackInRedux) {
            return selectedTimeTrackInRedux.itemDataId === itemDataId;
        }
        return false;
    }, [itemDataId, selectedTimeTrackInRedux]);

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const tille = useMemo(() => {
        return item.data.find(x => x.shouldUseAsTheFieldToCreateItem)?.value?.value ?? 'Not Available';
    }, [item.data]);

    const popoverContent = (
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
            <Box mr={1}>
                <TimeTrackerCounter itemDataId={itemDataId} />
            </Box>
            <TimeTrackerStartAndStopButton title={`#${item.ticketNo}: ${tille}`} itemDataId={itemDataId} />
        </Box>
    );

    return (
        <Box sx={{flexShrink: 0, ml: 0.75}}>
            <div ref={anchorRef}>
                {showPopper && (
                    <AppTooltip title="Timer">
                        <AppBlinkingIconButton
                            sx={{ml: 0.3, fontSize: '0.875rem'}}
                            shouldBlink={isConuterStarted && isSelectITemData}
                            onClick={handleToggle}
                        >
                            <Timer1 />
                        </AppBlinkingIconButton>
                    </AppTooltip>
                )}
            </div>
            {showPopper ? (
                <Popper
                    placement={popperPlacement}
                    open={open}
                    anchorEl={anchorRef.current}
                    transition
                    disablePortal={disablePortal}
                >
                    {({TransitionProps}) => (
                        <Transitions type="grow" position="top" in={open} {...TransitionProps}>
                            <Paper
                                elevation={5}
                                sx={{
                                    mr: 4,
                                    width: 240,
                                    borderRadius: 1.5,
                                    padding: 2,
                                }}
                            >
                                <ClickAwayListener onClickAway={handleClose}>
                                    <MainCard elevation={0} border={false} content={false}>
                                        {popoverContent}
                                    </MainCard>
                                </ClickAwayListener>
                            </Paper>
                        </Transitions>
                    )}
                </Popper>
            ) : (
                <>{popoverContent}</>
            )}
        </Box>
    );
};

export default TimeTrackerPopover;
